// eslint-disable-next-line no-unused-vars
import { createSlice, createAsyncThunk, current } from '@reduxjs/toolkit';
import { requestStarted, requestFinished } from '~/stores/redux/common/loadingSlice';
import diceApi from './dice.api';
import togglePublishedState from '~/api/togglePublishedState.api';
import { alertSwal } from '~/utils/helpers';
import { handleThunkException } from '~/services/thunk.service';

export const fetchDiceApi = createAsyncThunk(
    'heroSaga/dice/fetchDiceApi',
    async (navigate, { dispatch, rejectWithValue }) => {
        try {
            dispatch(requestStarted());
            const data = await diceApi.index();
            if (data) {
                dispatch(requestFinished());
                return data;
            }
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue([]);
        }
    },
);

export const toggleDicePublishedStateApi = createAsyncThunk(
    'heroSaga/dice/toggleDicePublishedStateApi',
    async (id, { dispatch, rejectWithValue }) => {
        try {
            await togglePublishedState(id);

            return id;
        } catch (error) {
            handleThunkException(error, dispatch);

            return rejectWithValue();
        }
    },
);

export const createDiceApi = createAsyncThunk(
    'heroSaga/dice/createDiceApi',
    async (navigate, { dispatch, rejectWithValue }) => {
        try {
            dispatch(requestStarted());
            const { data } = await diceApi.create();

            return data;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const storeDiceApi = createAsyncThunk(
    'heroSaga/dice/storeDiceApi',
    async (params, { dispatch, rejectWithValue }) => {
        const { navigate, values } = params;

        try {
            dispatch(requestStarted());
            const hero = await diceApi.store(values);
            alertSwal('success', 'Dice created successfully!');

            return hero;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue([]);
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const showDiceApi = createAsyncThunk(
    'diceSaga/dice/showDiceApi',
    async (params, { dispatch, rejectWithValue }) => {
        const { id, navigate } = params;

        try {
            dispatch(requestStarted());
            const { data } = await diceApi.show(id);

            return data;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const updateDiceApi = createAsyncThunk(
    'heroSaga/dice/updateDiceApi',
    async (params, { dispatch, rejectWithValue }) => {
        const { navigate, id, values } = params;

        try {
            dispatch(requestStarted());
            await diceApi.update(id, values);
            alertSwal('success', 'Dice updated successfully!');

            return values;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const deleteDiceApi = createAsyncThunk('heroSaga/dice/deleteDiceApi', async (params, { dispatch }) => {
    const { navigate, id } = params;

    try {
        await diceApi.delete(id);

        return id;
    } catch (error) {
        handleThunkException(error, dispatch, navigate);
    }
});

export const diceSlice = createSlice({
    name: 'heroSaga/dice',
    initialState: { rows: [], model: {}, editedModel: {}, pictureAttributeNames: [], boons: [], alignments: [] }, //model is initialValues that are used for formik.
    reducers: {
        clearEditedDice(state) {
            state.editedModel = {};
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchDiceApi.fulfilled, (state, { payload }) => {
                return {
                    ...state,
                    rows: payload,
                };
            })
            .addCase(fetchDiceApi.rejected, (state, { payload = [] }) => {
                state.rows = payload;
            });

        builder
            .addCase(toggleDicePublishedStateApi.fulfilled, (state, { payload }) => {
                const updateRow = state.rows.find((row) => row.id === payload);
                updateRow.isReady = !updateRow.isReady;
            })
            .addCase(toggleDicePublishedStateApi.rejected, () => {
                //do nothing
            });

        builder
            .addCase(createDiceApi.fulfilled, (state, { payload }) => {
                state.model = payload.model;
                state.pictureAttributeNames = payload.pictureAttributeNames;
                state.boons = payload.boons;
                state.alignments = payload.alignments;
            })
            .addCase(createDiceApi.rejected, () => {
                //
            });

        builder
            .addCase(storeDiceApi.fulfilled, (state) => {
                return {
                    ...state,
                    rows: [], //clear all rows so that api fetchAll will be called again in index page.
                };
            })
            .addCase(storeDiceApi.rejected, () => {
                //do nothing so that state will remain intact and component will not re-render.
            });

        builder
            .addCase(showDiceApi.fulfilled, (state, { payload }) => {
                state.editedModel = payload.model;
                state.pictureAttributeNames = payload.pictureAttributeNames;
                state.boons = payload.boons;
                state.alignments = payload.alignments;
            })
            .addCase(showDiceApi.rejected, () => {
                //
            });

        builder
            .addCase(updateDiceApi.fulfilled, (state, { payload }) => {
                state.editedModel = payload; //this is to make Update component re-render so that the checkDuplicateName() will have the latest `initialName`.

                if (state.rows.length === 0) return;

                const updatedRow = state.rows.find((row) => row.id === payload.id);
                updatedRow.name = payload.name;
                updatedRow.description = payload.description;
                updatedRow.boon = state.boons.find((boon) => boon.id === payload.boonId);
                updatedRow.alignment = state.alignments.find((alignment) => alignment.id === payload.alignmentId);
            })
            .addCase(updateDiceApi.rejected, () => {
                //do nothing so that state will remain intact and component will not re-render.
            });

        builder.addCase(deleteDiceApi.fulfilled, (state, { payload }) => {
            return {
                ...state,
                rows: state.rows.filter(({ id }) => id !== payload),
            };
        });
    },
});

export const { clearEditedDice } = diceSlice.actions;
